import { z } from 'zod';

// Setter spec §07.5–§07.10, §13.4, C13: one consolidated weekly report for Estateflow (global + per developer/workspace
// + per project, week vs previous week) delivered to Notion (full page) and Slack (executive summary + link), and the
// supervised-learning queue (proposals from real conversations → commercial + technical approval → NEW draft, never
// auto-published). Platform-level: platform capabilities only. Unknown ≠ 0 (null = sin datos); scheduled ≠ held.

const Iso = z.string();
const Id = z.string().min(1).max(64);
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha AAAA-MM-DD');
/** null = sin datos / no disponible (never rendered as 0). */
const N = z.number().nullable();

// ───────────── Metrics ─────────────

export const WeeklyCostSchema = z.object({
  currency: z.string().length(3),
  /** Amounts reported by a provider (measured usage with a cost). */
  realMinor: z.string().regex(/^\d+$/),
  /** Amounts computed from configured rates (§16 estimates). */
  estimatedMinor: z.string().regex(/^\d+$/),
});
export type WeeklyCost = z.infer<typeof WeeklyCostSchema>;

export const WeeklyUnitCostSchema = z.object({
  currency: z.string().length(3),
  amountMinor: z.string().regex(/^\d+$/),
  /** True when part of the cost is an estimate. */
  includesEstimates: z.boolean(),
});

export const WeeklyMetricsSchema = z.object({
  // Funnel (cohort = opportunities created in the week; unique leads = distinct contacts behind them).
  uniqueLeads: z.number().int(),
  opportunities: z.number().int(),
  contacted: z.number().int(),
  openConversations: z.number().int(),
  qualified: z.number().int(),
  // Appointments: scheduled (booked in the week) ≠ held (visit in the week, outcome reported as completed).
  booked: z.number().int(),
  held: z.number().int(),
  noShow: z.number().int(),
  /** Visits in the week already past without a reported outcome (unknown, never counted as held or no-show). */
  pendingOutcome: z.number().int(),
  /** held ÷ (held + noShow); null without reported outcomes. */
  showRate: N,
  cancellations: z.number().int(),
  recoveries: z.number().int(),
  // Speed (form/new leads of the cohort): seconds from opportunity creation to the first outbound attempt.
  firstAttemptP50: N,
  firstAttemptP95: N,
  firstAttemptMeasured: z.number().int(),
  notAttempted: z.number().int(),
  slaWithin5Min: z.number().int(),
  /** slaWithin5Min ÷ (firstAttemptMeasured + notAttempted); null without cohort. */
  slaRate: N,
  // Usage.
  calls: z.number().int(),
  callMinutes: N,
  callsWithoutDuration: z.number().int(),
  waOutbound: z.number().int(),
  waInbound: z.number().int(),
  // Costs (real vs estimated kept apart; events without a cost are unknown, not 0).
  costs: z.array(WeeklyCostSchema),
  uncostedEvents: z.number().int(),
  costPerHeld: z.array(WeeklyUnitCostSchema),
  costPerConversation: z.array(WeeklyUnitCostSchema),
  // Quality and risks.
  incidents: z.number().int(),
  opsIncidents: z.number().int(),
  /** AI abuse flags raised in the period (Wave 4A); null only in snapshots generated before the registry existed. */
  abuseFlags: N,
  excessiveCalls: z.number().int(),
  casesToReview: z.number().int(),
});
export type WeeklyMetrics = z.infer<typeof WeeklyMetricsSchema>;

export const WEEKLY_REPORT_LEVELS = ['global', 'workspace', 'project'] as const;
export const WeeklyReportRowSchema = z.object({
  level: z.enum(WEEKLY_REPORT_LEVELS),
  workspaceId: z.string().nullable(),
  workspaceName: z.string().nullable(),
  projectId: z.string().nullable(),
  projectName: z.string().nullable(),
  current: WeeklyMetricsSchema,
  previous: WeeklyMetricsSchema,
});
export type WeeklyReportRow = z.infer<typeof WeeklyReportRowSchema>;

export const REVIEW_CASE_KINDS = ['open_incidents', 'pending_outcomes', 'budget_reviews', 'open_handoffs', 'excessive_calls', 'learning_proposals'] as const;
export type ReviewCaseKind = (typeof REVIEW_CASE_KINDS)[number];
export const REVIEW_CASE_LABELS: Record<ReviewCaseKind, string> = {
  open_incidents: 'Incidencias abiertas',
  pending_outcomes: 'Citas pasadas sin resultado reportado',
  budget_reviews: 'Prospectos en revisión por presupuesto de IA',
  open_handoffs: 'Transferencias a persona sin atender',
  excessive_calls: 'Llamadas con uso excesivo de minutos',
  learning_proposals: 'Propuestas de aprendizaje por revisar',
};
export const ReviewCaseSchema = z.object({
  kind: z.enum(REVIEW_CASE_KINDS),
  workspaceId: z.string().nullable(),
  workspaceName: z.string().nullable(),
  count: z.number().int(),
});

export const WeeklyReportSchema = z.object({
  weekStart: LocalDate,
  weekEnd: LocalDate,
  timezone: z.string(),
  period: z.object({ start: Iso, end: Iso }),
  previousPeriod: z.object({ start: Iso, end: Iso }),
  generatedAt: Iso,
  global: WeeklyReportRowSchema,
  workspaces: z.array(WeeklyReportRowSchema),
  projects: z.array(WeeklyReportRowSchema),
  reviewCases: z.array(ReviewCaseSchema),
  learning: z.array(z.object({ id: z.string(), title: z.string(), status: z.string(), workspaceName: z.string().nullable(), projectName: z.string().nullable(), occurrences: z.number().int() })),
  /** Workspaces left out (demo) and other honest caveats. */
  notes: z.array(z.string()),
  definitions: z.record(z.string(), z.string()),
});
export type WeeklyReport = z.infer<typeof WeeklyReportSchema>;

// ───────────── Settings + destinations ─────────────

/** ISO weekday (1 = lunes) → label. */
export const ISO_WEEKDAY_LABELS: Record<number, string> = { 1: 'Lunes', 2: 'Martes', 3: 'Miércoles', 4: 'Jueves', 5: 'Viernes', 6: 'Sábado', 7: 'Domingo' };

export const DeliveryDestinationSchema = z.object({
  connected: z.boolean(),
  /** Set only when a real test call succeeded; until then «sin verificar». */
  verifiedAt: Iso.nullable(),
  lastError: z.string().nullable(),
});

export const WeeklyReportSettingsSchema = z.object({
  enabled: z.boolean(),
  weekday: z.number().int().min(1).max(7),
  hour: z.number().int().min(0).max(23),
  minute: z.number().int().min(0).max(59),
  timezone: z.string(),
  excessiveCallMinutes: z.number().int().min(1).max(120),
  nextRunAt: Iso.nullable(),
  notion: DeliveryDestinationSchema.extend({ databaseId: z.string().nullable(), titleProperty: z.string() }),
  slack: DeliveryDestinationSchema.extend({ label: z.string().nullable() }),
});
export type WeeklyReportSettings = z.infer<typeof WeeklyReportSettingsSchema>;

export const PutWeeklyReportSettingsSchema = z.object({
  enabled: z.boolean(),
  weekday: z.number().int().min(1).max(7).default(1),
  hour: z.number().int().min(0).max(23).default(9),
  minute: z.number().int().min(0).max(59).default(0),
  timezone: z.string().min(1).max(64).default('America/Mexico_City'),
  excessiveCallMinutes: z.number().int().min(1).max(120).default(12),
});

export const ConnectNotionSchema = z.object({
  /** Notion internal integration token (secret_… / ntn_…); stored encrypted, never returned. */
  token: z.string().trim().min(20).max(300).regex(/^(secret_|ntn_)[A-Za-z0-9]+$/, 'Debe ser un token de integración interna de Notion (secret_… o ntn_…)'),
  databaseId: z.string().trim().regex(/^[0-9a-fA-F-]{32,36}$/, 'ID de base de datos de Notion (32 caracteres hexadecimales)'),
  titleProperty: z.string().trim().min(1).max(100).default('Name'),
});
export const ConnectWeeklySlackSchema = z.object({
  webhookUrl: z
    .string()
    .trim()
    .max(500)
    .refine((u) => /^https:\/\/hooks\.slack\.com\/services\/[A-Za-z0-9/_-]+$/.test(u), 'Debe ser una URL de Incoming Webhook de Slack (https://hooks.slack.com/services/…)'),
  label: z.string().trim().min(1).max(80),
});
export const DestinationTestResultSchema = z.object({ ok: z.boolean(), detail: z.string(), settings: WeeklyReportSettingsSchema });

// ───────────── Runs ─────────────

export const WEEKLY_RUN_STATUSES = ['pending', 'generated', 'failed'] as const;
export const WEEKLY_DELIVERY_STATUSES = ['pending', 'sent', 'failed', 'not_configured'] as const;
export type WeeklyDeliveryStatus = (typeof WEEKLY_DELIVERY_STATUSES)[number];
export const WEEKLY_DELIVERY_STATUS_LABELS: Record<WeeklyDeliveryStatus, string> = {
  pending: 'Pendiente',
  sent: 'Enviado (sin verificar)',
  failed: 'Falló',
  not_configured: 'Sin configurar',
};

export const WeeklyReportRunSchema = z.object({
  id: Id,
  weekStart: LocalDate,
  trigger: z.enum(['scheduled', 'manual']),
  status: z.enum(WEEKLY_RUN_STATUSES),
  error: z.string().nullable(),
  notionStatus: z.enum(WEEKLY_DELIVERY_STATUSES),
  notionUrl: z.string().nullable(),
  notionDetail: z.string().nullable(),
  slackStatus: z.enum(WEEKLY_DELIVERY_STATUSES),
  slackDetail: z.string().nullable(),
  requestedByName: z.string().nullable(),
  generatedAt: Iso.nullable(),
  createdAt: Iso,
});
export type WeeklyReportRun = z.infer<typeof WeeklyReportRunSchema>;
export const WeeklyReportRunDetailSchema = WeeklyReportRunSchema.extend({ report: WeeklyReportSchema.nullable() });
export const CreateWeeklyReportRunSchema = z.object({
  /** Any date inside the week to report (local, settings timezone); default = the last complete week. */
  weekOf: LocalDate.optional(),
});

// ───────────── Supervised learning ─────────────

export const LEARNING_KINDS = ['question', 'objection', 'loss_reason', 'no_show'] as const;
export type LearningKind = (typeof LEARNING_KINDS)[number];
export const LEARNING_KIND_LABELS: Record<LearningKind, string> = {
  question: 'Pregunta frecuente',
  objection: 'Objeción frecuente',
  loss_reason: 'Motivo de descarte',
  no_show: 'Citas no realizadas',
};
export const LEARNING_STATUSES = ['proposed', 'approved', 'rejected', 'implemented'] as const;
export type LearningStatus = (typeof LEARNING_STATUSES)[number];
export const LEARNING_STATUS_LABELS: Record<LearningStatus, string> = {
  proposed: 'Propuesta',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  implemented: 'Implementada como borrador',
};
export const LEARNING_TARGETS = ['project_prompt', 'global_prompt', 'knowledge'] as const;
export type LearningTarget = (typeof LEARNING_TARGETS)[number];
export const LEARNING_TARGET_LABELS: Record<LearningTarget, string> = {
  project_prompt: 'Borrador de instrucciones del proyecto',
  global_prompt: 'Borrador de instrucciones globales',
  knowledge: 'Borrador de conocimiento (documento en revisión)',
};
export const PROJECT_PROMPT_FIELDS = ['projectFacts', 'developerContext', 'escalationRules', 'whatsappStyle', 'voiceStyle', 'mainPrompt'] as const;
export const GLOBAL_PROMPT_FIELDS = ['base', 'whatsapp', 'voice', 'toolUse'] as const;
export const APPROVAL_ROLES = ['commercial', 'technical'] as const;
export type ApprovalRole = (typeof APPROVAL_ROLES)[number];
export const APPROVAL_ROLE_LABELS: Record<ApprovalRole, string> = { commercial: 'Aprobación comercial', technical: 'Viabilidad técnica' };

export const LearningEvidenceSchema = z.object({
  kind: z.enum(['conversation', 'call', 'opportunity', 'appointment']),
  id: z.string(),
  workspaceId: z.string(),
  projectId: z.string().nullable(),
  at: Iso.nullable(),
  /** Short masked excerpt (digits/emails hidden), never a full transcript. */
  excerpt: z.string().nullable(),
  /** Deep link inside LIA (opens only for people authorized in that workspace). */
  link: z.string(),
});
export type LearningEvidence = z.infer<typeof LearningEvidenceSchema>;

const Approval = z.object({ userId: z.string(), name: z.string(), at: Iso, note: z.string().nullable() }).nullable();

export const LearningProposalSchema = z.object({
  id: Id,
  workspaceId: z.string().nullable(),
  workspaceName: z.string().nullable(),
  projectId: z.string().nullable(),
  projectName: z.string().nullable(),
  kind: z.enum(LEARNING_KINDS),
  topic: z.string(),
  weekStart: LocalDate,
  title: z.string(),
  summary: z.string(),
  suggestion: z.string(),
  suggestedTarget: z.enum(LEARNING_TARGETS),
  occurrences: z.number().int(),
  evidence: z.array(LearningEvidenceSchema),
  status: z.enum(LEARNING_STATUSES),
  commercial: Approval,
  technical: Approval,
  rejection: z.object({ userId: z.string(), name: z.string(), at: Iso, reason: z.string() }).nullable(),
  implemented: z
    .object({
      target: z.enum(LEARNING_TARGETS),
      field: z.string().nullable(),
      ref: z.string(),
      version: z.number().int().nullable(),
      by: z.string(),
      at: Iso,
      testResult: z.string().nullable(),
      note: z.string(),
    })
    .nullable(),
  createdAt: Iso,
});
export type LearningProposal = z.infer<typeof LearningProposalSchema>;

export const LearningProposalQuerySchema = z.object({
  status: z.enum(LEARNING_STATUSES).optional(),
  workspaceId: Id.optional(),
});
export const GenerateLearningSchema = z.object({ weekOf: LocalDate.optional() });
export const GenerateLearningResultSchema = z.object({ weekStart: LocalDate, created: z.number().int(), existing: z.number().int() });
export const ApproveLearningSchema = z.object({ role: z.enum(APPROVAL_ROLES), note: z.string().trim().max(1000).nullable().optional() });
export const RejectLearningSchema = z.object({ role: z.enum(APPROVAL_ROLES), reason: z.string().trim().min(5).max(1000) });
export const ImplementLearningSchema = z.object({
  target: z.enum(LEARNING_TARGETS),
  /** project_prompt: PROJECT_PROMPT_FIELDS; global_prompt: GLOBAL_PROMPT_FIELDS; knowledge: ignored. */
  field: z.string().max(40).nullable().optional(),
  /** Text appended to the draft (or the knowledge document body). */
  text: z.string().trim().min(10).max(8000),
  /** Knowledge only: document title. */
  title: z.string().trim().min(3).max(200).nullable().optional(),
  testResult: z.string().trim().max(2000).nullable().optional(),
});
